/**
 * Success Page
 *
 * Displays confirmation after successful submission.
 */

import { useParams, useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  ContentCopy as CopyIcon,
  Print as PrintIcon,
  TrackChanges as StatusIcon,
} from '@mui/icons-material';

interface SubmissionState {
  referenceNumber: string;
  submittedAt: string;
  authorityName: string;
  returnChannelEnabled?: boolean;
}

export function SuccessPage() {
  const { authorityKeyId, processKey } = useParams<{
    authorityKeyId: string;
    processKey: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as SubmissionState | null;

  // Redirect if no state (direct access)
  if (!state) {
    return <Navigate to={`/submit/${authorityKeyId}/${processKey}`} replace />;
  }

  const handleCopyReference = () => {
    navigator.clipboard.writeText(state.referenceNumber);
  };

  const handlePrint = () => {
    window.print();
  };

  const formattedDate = new Date(state.submittedAt).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {/* Success Icon */}
          <CheckCircleIcon
            sx={{
              fontSize: 80,
              color: 'success.main',
              mb: 2,
            }}
          />

          <Typography variant="h4" component="h1" gutterBottom>
            Antrag erfolgreich eingereicht
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph>
            Ihr Antrag wurde verschlüsselt an <strong>{state.authorityName}</strong> übermittelt.
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Reference Number */}
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 3, mb: 3 }}>
            <Typography variant="overline" color="text.secondary">
              Ihre Referenznummer
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontFamily: 'monospace', fontWeight: 'bold', my: 1 }}
            >
              {state.referenceNumber}
            </Typography>
            <Button
              size="small"
              startIcon={<CopyIcon />}
              onClick={handleCopyReference}
            >
              Kopieren
            </Button>
          </Box>

          {/* Submission Details */}
          <Box sx={{ textAlign: 'left', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Eingereicht am:</strong> {formattedDate}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Empfänger:</strong> {state.authorityName}
            </Typography>
          </Box>

          {/* Encryption Confirmation */}
          <Alert
            icon={<LockIcon />}
            severity="success"
            sx={{ textAlign: 'left', mb: 3 }}
          >
            <Typography variant="body2">
              <strong>Ende-zu-Ende-Verschlüsselung bestätigt</strong>
              <br />
              Ihre Daten wurden in Ihrem Browser verschlüsselt und können nur von der
              Behörde entschlüsselt werden.
            </Typography>
          </Alert>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {state.returnChannelEnabled !== false && (
              <Button
                variant="contained"
                startIcon={<StatusIcon />}
                onClick={() => navigate(`/status?ref=${state.referenceNumber}`)}
              >
                Status prüfen
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Bestätigung drucken
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 4 }}
          >
            {state.returnChannelEnabled !== false
              ? 'Bitte notieren Sie sich die Referenznummer. Sie benötigen diese für Rückfragen bei der Behörde.'
              : 'Bitte notieren Sie sich die Referenznummer für Ihre Unterlagen.'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
